import {optionalMaterialInput} from "#material/nodes/base/connection/validation/parameters"
import type {MaterialNodeDefinition} from "#material/nodes/base/material/types/definition"
import {ExchangeNodeName} from "#material/exchange/node-name"
import {materialFloatOutput} from "#material/nodes/base/material/validation/io"
import {materialUiSchema} from "#material/nodes/base/material/validation/ui"
import type {MaterialNodeSockets} from "#material/types/material-node-sockets"
import {vec3} from "#material/types/vector"
import {z} from "zod/v4-mini"

export interface LayerWeightSockets extends MaterialNodeSockets {
    inputs: {
        blend: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    settings: {
        blend: "float" // found in inputs: -; found in settings: float; inferred from data: float
        facing: "float" // found in inputs: -; found in settings: float; inferred from data: float
        fresnel: "float" // found in inputs: -; found in settings: float; inferred from data: float
        normal: "normal" // found in inputs: -; found in settings: vector; inferred from data: float3
    }
    outputs: {
        fresnel: "float"
        facing: "float"
    }
}

export const layerWeightDefinition = {
    exchangeFormat: {
        nodeName: ExchangeNodeName.ShaderNodeLayerWeight,
    },
    nodeClass: "LayerWeight",
    ui: {
        label: "Layer Weight",
        color: "#2d8a5d",
        items: [
            {name: "fresnel", label: "Fresnel", valueType: "float", output: true},
            {name: "facing", label: "Facing", valueType: "float", output: true},
            {name: "blend", label: "Blend", valueType: "float", range: {min: 0, max: 1}, input: true, setting: true, defaultValue: 0.5},
            {name: "normal", label: "Normal", valueType: "normal", input: true, setting: true, defaultValue: {x: 0, y: 0, z: 0}},
        ],
        support: {
            cycles: true,
            three: true,
        },
    },
    validation: {
        returns: z.strictObject({fresnel: materialFloatOutput, facing: materialFloatOutput}),
        parameters: z.strictObject({
            inputs: z.strictObject({blend: optionalMaterialInput, normal: optionalMaterialInput}),
            settings: z.strictObject({
                blend: z.optional(z.number()),
                facing: z.optional(z.number()),
                fresnel: z.optional(z.number()),
                normal: z.optional(vec3),
            }),
            ui: materialUiSchema,
        }),
    },
} satisfies MaterialNodeDefinition<LayerWeightSockets>
